import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  normalizeVoucherCategories,
  useVoucherContext,
} from "../../Context/Voucher/VoucherContext";
import { Voucher } from "../../Components/Voucher/Voucher";
import { useGetVouchersList } from "../../APICalling/useApiQueries";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "../../Components/Service/Loader";
import { Header } from "../../Components/Header/Header";
import { useSticky } from "../../Utils/Helper";
import { clearSessionStorage } from "../../Utils/SessionStorage";
import styles from "./Style.module.css";
import Placeholder from "../../Components/Placeholder/Placeholder";
import CloseCircleIcon from "../../Components/Icons/CloseCircleIcon";
import VoucherCart from "../../Components/Cart/VoucherCart";
import { VoucherTermsModal } from "../../Components/Popup/VoucherTermsModal/VoucherTermsModal";

/** Resolve selected qty for a voucher id (supports slot lines and grouped count). */
function getSelectedCountForId(cartVouchers, voucherId) {
  const matching = cartVouchers.filter(
    (v) => String(v.id) === String(voucherId)
  );
  if (matching.length === 0) return 0;

  const maxCount = matching.reduce(
    (max, v) => Math.max(max, Number(v.count) || 0),
    0
  );
  return Math.max(matching.length, maxCount);
}

/** Sync category voucher counters with the selected voucher cart. */
function mergeVoucherListWithSelection(prevList, selectedVouchers) {
  const cartVouchers = Array.isArray(selectedVouchers) ? selectedVouchers : [];

  if (!Array.isArray(prevList) || prevList.length === 0) return prevList;

  if (cartVouchers.length === 0) {
    return prevList.map((item) => ({
      ...item,
      vouchers: Array.isArray(item.vouchers)
        ? item.vouchers.map((voucher) => ({
            ...voucher,
            count: 0,
            isCounterVisible: false,
          }))
        : [],
    }));
  }

  return prevList.map((item) => ({
    ...item,
    vouchers: Array.isArray(item.vouchers)
      ? item.vouchers.map((voucher) => {
          const slotCount = getSelectedCountForId(cartVouchers, voucher.id);
          return {
            ...voucher,
            count: slotCount,
            isCounterVisible: slotCount > 0,
          };
        })
      : [],
  }));
}

export const SelectVouchers = () => {
  const {
    voucherList,
    setVoucherList,
    selectedVouchers,
    voucherCount,
    voucherTotal,
    isVoucherCartActive,
    clearVoucherCart,
  } = useVoucherContext();

  const [activeCategory, setActiveCategory] = useState(null);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [selectedTerms, setSelectedTerms] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const stickyHeaderRef = useRef(null);
  const { isSticky, sentinelRef } = useSticky({ headerRef: stickyHeaderRef });

  const handleBack = () => {
    clearVoucherCart();
    clearSessionStorage();
    navigate('/');
  };

  const categoryRefs = useRef(new Map());
  const hasRestoredCategoryRef = useRef(false);
  const scrollToCategoryId = location.state?.scrollToCategoryId;

  const selectedVouchersRef = useRef(selectedVouchers);
  selectedVouchersRef.current = selectedVouchers;

  const setCategoryRef = useCallback((categoryId) => (el) => {
    const map = categoryRefs.current;
    if (el) map.set(categoryId, el);
    else map.delete(categoryId);
  }, []);

  const queryParams = useMemo(() => ({
    pagination: 0,
    is_image: 1,
    is_online: 1,
  }), []);

  const { data: queryData, isLoading: loading, isError } = useGetVouchersList(queryParams);

  useEffect(() => {
    if (loading || isError || !queryData) return;

    const voucherCategoryData = queryData?.data?.data?.list?.category || [];
    const apiData = normalizeVoucherCategories(voucherCategoryData).map(
      (category) => ({
        ...category,
        vouchers: Array.isArray(category.vouchers)
          ? category.vouchers.map((voucher) => ({
              ...voucher,
              count: 0,
              isCounterVisible: false,
            }))
          : [],
      })
    );

    // Apply current cart immediately so counters are selected after edit navigation.
    setVoucherList(
      mergeVoucherListWithSelection(apiData, selectedVouchersRef.current)
    );
    setActiveCategory((prev) => apiData.some((category) => category.id === prev)
      ? prev : (apiData[0]?.id ?? null));
  }, [queryData, loading, isError, setVoucherList]);

  const selectedVouchersKey = useMemo(
    () => JSON.stringify(selectedVouchers ?? []),
    [selectedVouchers]
  );

  // Keep catalog counters in sync when cart changes (and after load).
  useLayoutEffect(() => {
    if (loading) return;
    setVoucherList((prevList) => {
      if (!prevList?.length) return prevList;
      return mergeVoucherListWithSelection(prevList, selectedVouchersRef.current);
    });
  }, [loading, selectedVouchersKey, setVoucherList]);

  useEffect(() => {
    if (activeCategory == null) return;
    const btn = categoryRefs.current.get(activeCategory);
    if (!btn) return;
    const container = btn.parentElement;
    if (!container || container.scrollWidth <= container.clientWidth) return;
    const cRect = container.getBoundingClientRect();
    const bRect = btn.getBoundingClientRect();
    if (bRect.left < cRect.left) container.scrollLeft += bRect.left - cRect.left;
    else if (bRect.right > cRect.right) container.scrollLeft += bRect.right - cRect.right;
  }, [activeCategory]);

  const handleCategoryClick = useCallback((categoryId) => {
    // Tabs replace content; they must not be reselected by page scrolling.
    // Avoid a smooth scroll racing the new category's height and sticky header.
    const root = document.documentElement;
    const previousScrollBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = "auto";
    window.scrollTo({ top: 0, behavior: "auto" });
    root.style.scrollBehavior = previousScrollBehavior;
    setActiveCategory(categoryId);
  }, []);

  // Restore the category requested by Edit on Billing Details, even when its
  // ID was serialized as a string. The target section need not be mounted yet.
  useEffect(() => {
    if (loading || scrollToCategoryId == null || hasRestoredCategoryRef.current) return;
    const category = voucherList.find((item) => String(item.id) === String(scrollToCategoryId));
    if (!category) return;
    hasRestoredCategoryRef.current = true;
    handleCategoryClick(category.id);
    navigate(".", { replace: true, state: {} });
  }, [loading, scrollToCategoryId, voucherList, handleCategoryClick, navigate]);

  const noVoucherSelected = useMemo(() => {
    return Number(voucherCount ?? 0) === 0;
  }, [voucherCount]);

  const showCartPadding = isVoucherCartActive;
  const cartDisplayTotal = voucherTotal ?? 0;
  const cartDisplayCount = voucherCount ?? 0;
  const voucherListLength = voucherList?.length ?? 0;

  const handleReadTerms = (termsText) => {
    setSelectedTerms(termsText || "");
    setShowTermsModal(true);
  };

  return (
    <>
      <VoucherTermsModal
        active={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        termsText={selectedTerms}
      />
      <div className="sticky-sentinel" ref={sentinelRef} aria-hidden="true" />
      <div ref={stickyHeaderRef} className={`sticky-header ${isSticky ? "is-sticky" : ""} pb-0`}>
        <Header
          title={voucherListLength > 0 ? `Select Vouchers` : "Vouchers"}
          noVoucherSelected={noVoucherSelected}
          onBack={handleBack}
        />
        {voucherList.length > 0 && (
          <div className={`${styles.voucherCategoryList} container-md space-x hide-scrollbar`}>
            {voucherList.map((category) => (
              <button
                key={category.id}
                type="button"
                ref={setCategoryRef(category.id)}
                className={`${styles.voucherCategoryBtn}${
                  activeCategory === category.id ? ` ${styles.isActive}` : ""
                }`}
                onClick={() => handleCategoryClick(category.id)}
                title={category.name}
                aria-label={category.name}
                aria-pressed={activeCategory === category.id}
              >
                {category.name}
              </button>
            ))}
          </div>
        )}
      </div>

      <div
        className={`space-x space-y container-md ${
          showCartPadding ? "cart-space-b" : ""
        }`}
      >
        {loading ? (
          <Loader />
        ) : (
          <div>
            {voucherList.length > 0 ? (
              voucherList.filter((category) => category.id === activeCategory).map((category) => (
                <div key={category.id} role="region" aria-label={category.name}>
                  <Voucher
                    id={category?.id}
                    title={category?.name}
                    description={category?.description}
                    image={category?.image}
                    vouchers={category?.vouchers}
                    terms={category?.terms}
                    termsAndCondition={category?.terms_and_condition}
                    onReadTerms={handleReadTerms}
                  />
                </div>
              ))
            ) : (
              <Placeholder
                heading="Vouchers not found"
                para="Please try again later"
                icon={<CloseCircleIcon />}
              />
            )}
          </div>
        )}
      </div>

      <VoucherCart
        btnLabel="Add"
        btnClick={() => navigate("/billing-details")}
        isCartActive={showCartPadding}
        disabled={false}
        displayTotal={cartDisplayTotal}
        displayCount={cartDisplayCount}
      />
    </>
  );
};
